import React, { useEffect, useState } from 'react';
import { Button, Input, Text, Icon } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';
import type { Artifact } from '../RequirementGroups.types';

interface RenameArtifactPanelProps {
  isOpen: boolean;
  artifact: Artifact | null;
  onClose: () => void;
  onSave: (artifactId: string, newTitle: string) => Promise<void>;
  isSaving: boolean;
}

export const RenameArtifactPanel: React.FC<RenameArtifactPanelProps> = ({
  isOpen,
  artifact,
  onClose,
  onSave,
  isSaving,
}) => {
  const [title, setTitle] = useState('');
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (artifact) {
      setTitle(artifact.title || '');
    }
  }, [artifact]);

  const handleSave = async () => {
    if (!artifact || !title.trim()) return;
    
    try {
      await onSave(artifact.id, title.trim());
      handleClose();
    } catch (error) {
      console.error('Failed to rename artifact:', error);
    }
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      setTitle('');
      onClose();
    }, 300);
  };

  if (!isOpen && !isClosing) return null;

  return (
    <div 
      className={`fixed inset-0 bg-alpha-black-70 z-50 ${isClosing ? 'animate-fade-out' : 'animate-fade-in'}`} 
      onClick={handleClose}
    >
      <div
        className={`absolute right-0 top-0 h-full bg-bg-primary w-[400px] flex flex-col border-l border-border-secondary-alt ${isClosing ? 'animate-slide-out-right' : 'animate-slide-in-right'}`}
        onClick={(e) => e.stopPropagation()}
        style={{
          boxShadow: "0 20px 24px -4px rgba(10, 13, 18, 0.08), 0 8px 8px -4px rgba(10, 13, 18, 0.03), 0 3px 3px -1.5px rgba(10, 13, 18, 0.04)"
        }}
      >
        {/* Top Section */}
        <div className="flex p-bg-v2-3xl items-center gap-bg-v2-md self-stretch justify-between">
          <div>
            <Text variant="text-xl" weight="bold">Update Title</Text>
          </div>
          <button
            onClick={handleClose}
            className="flex items-center justify-center w-8 h-8 rounded-full text-fg-secondary hover:bg-bg-primary-hover hover:text-text-secondary-hover transition-all duration-200"
            disabled={isSaving}
          >
            <Icon name={IconName.Cross} size="lg" inheritColor />
          </button>
        </div>

        {/* Divider */}
        <div className="box-border flex flex-col gap-[16px] items-center px-[var(--spacing-3xl,24px)] py-0 w-full">
          <div className="bg-border-secondary h-px w-full" />
        </div>

        <form onSubmit={(e) => { e.preventDefault(); handleSave(); }} className="flex flex-col">
          {/* Middle Section */}
          <div className="flex flex-col items-center gap-bg-v2-3xl self-stretch px-bg-v2-3xl py-bg-v2-3xl overflow-y-auto">
            <div className="w-full space-y-6">
              <Input
                id="artifact-title"
                name="artifact-title"
                label="Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter artifact title"
                className="w-full"
                autoFocus
                required
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !isSaving && title.trim()) {
                    handleSave();
                  } else if (e.key === 'Escape') {
                    handleClose();
                  }
                }}
              />
            </div>
          </div>

          {/* Last Section */}
          <div className="flex p-bg-v2-xl px-bg-v2-3xl justify-end items-center gap-bg-v2-xl self-stretch border-t border-border-secondary bg-bg-primary">
            <Button
              variant="outline"
              label="Cancel"
              type="button"
              onClick={handleClose}
              disabled={isSaving}
            />
            <Button
              variant="primary"
              type="submit"
              label="Save Changes"
              disabled={isSaving || !title.trim()}
              loading={isSaving}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

